import { SIDEBAR_WIDTH, appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useAccessToken } from "@/hooks";
import {
  getTwitchUser,
  twitchLogin,
  useGetTwitchDrops,
} from "@/hooks/quest/twitchDropHooks";
import { useTopHeroScrollMotion } from "@/hooks/useTopHeroScrollMotion";
import { notifyError } from "@/utils/monitoring/newrelic";
import { authorizedPost } from "@/utils/request";
import { trackLegacyEvent } from "@analytics";
import {
  AnimateHeightBox,
  Body,
  Box,
  BulletList,
  Button,
  Chip,
  ConfirmationDialog,
  CountdownTimer,
  Divider,
  Grid,
  Heading,
  HeroBackground,
  HeroCard,
  Icon,
  Link,
  Stack,
  centerFlexChildren,
  hFlex,
  mediaQuerySizesHelper,
  useIntersectionObserver,
  usePrevious,
  useTheme,
} from "@biom3/react";
import { getWebLink } from "@cms/utils";
import { VideoQuestModal } from "@components/QuestModals/VideoQuestModal";
import type { Content } from "@prismicio/client";
import type { GameSpecifications } from "@ui-kit/GameSpecs";
import { mapPlatformIcons } from "@ui-kit/GameSpecs/Platforms";
import { PremiumGameHeader } from "@ui-kit/PremiumGameHeader";
import type { PlayVideoMimeType } from "@ui-kit/QuestList";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import { AxiosError } from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import dynamic from "next/dynamic";
import Image from "next/image";
import NextLink from "next/link";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { merge } from "ts-deepmerge";

const TWITCH_PURPLE = "#a855f7";

interface TwitchDrop {
  id: string;
  name: string;
  description: string;
  image?: string;
}

// supported game UID's
const HELLMASTER_GAME_UID = "hellmaster";
const BATTLE_RISE_GAME_UID = "battlerise";
export const ELUMIA_GAME_UID = "elumia";
export const ARMOURX_GAME_UID = "armourx";
export const METATOY_GAME_UID = "mtdz";

const OVERTAKE_TWITCH_DROPS: TwitchDrop[] = [
  {
    id: "949450b8-5c64-11f0-8db5-0a58a9feac02",
    name: "100,000 Shards",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/hellmaster-twitch-drops/shard.png`,
  },
  {
    id: "dca5cd27-5c64-11f0-9e24-0a58a9feac02",
    name: "1 Key",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/hellmaster-twitch-drops/onekey.png`,
  },
  {
    id: "e5f5360c-5c64-11f0-811d-0a58a9feac02",
    name: "2 Keys",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/hellmaster-twitch-drops/twokeys.png`,
  },
  {
    id: "ef7a5ccc-5c64-11f0-bf13-0a58a9feac02",
    name: "3 Keys",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/hellmaster-twitch-drops/threekeys.png`,
  },
];

const BATTLE_RISE_TWITCH_DROPS: TwitchDrop[] = [
  {
    id: "BR1",
    name: "Starter Box",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/battlerise-twitch-drops/BR1.png`,
  },
  {
    id: "BR2",
    name: "Advanced Box",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/battlerise-twitch-drops/BR2.png`,
  },
  {
    id: "BR3",
    name: "Elite Box",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/battlerise-twitch-drops/BR3.png`,
  },
  {
    id: "BR4",
    name: "50 Shards",
    description: "",
    image: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/images/battlerise-twitch-drops/BR4.png`,
  },
];

const ELUMIA_TWITCH_DROPS: TwitchDrop[] = [
  {
    id: "bc103dc9-7749-11f0-9254-0a58a9feac02",
    name: "Bronze Chest",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/elumia/A1_Bronze_Chest_Token.png`,
  },
  {
    id: "c60efd84-7749-11f0-86ca-0a58a9feac02",
    name: "Gold Chest",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/elumia/A2_Gold_Chest_Token.png`,
  },
  {
    id: "cec1f713-7749-11f0-bbab-0a58a9feac02",
    name: "Platinum Chest",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/elumia/A3_Platinum_Chest_Token.png`,
  },
  {
    id: "d8664c54-7749-11f0-886e-0a58a9feac02",
    name: "Diamond Chest",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/elumia/A4_Diamond_Chest_Token.png`,
  },
];

const ARMOURX_TWITCH_DROPS: TwitchDrop[] = [
  {
    id: "e310493e-82e2-11f0-a3ee-0a58a9feac02",
    name: "Bronze Box",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/armourx/AX_ENERGY_CUBE_BRONZE.png`,
  },
  {
    id: "43fba05b-82e3-11f0-8182-0a58a9feac02",
    name: "Silver Box",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/armourx/AX_ENERGY_CUBE_SILVER.png`,
  },
  {
    id: "507720c9-82e3-11f0-8299-0a58a9feac02",
    name: "Gold Box",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/armourx/AX_ENERGY_CUBE_GOLD.png`,
  },
  {
    id: "613c3947-82e3-11f0-addd-0a58a9feac02",
    name: "Platinum Box",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/armourx/AX_ENERGY_CUBE_PLATINUM.png`,
  },
];

const METATOY_TWITCH_DROPS: TwitchDrop[] = [
  {
    id: "lv4-battery",
    name: "Lv4 Battery",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/metatoy/Twitch_Drops_15_Min.jpg`,
  },
  {
    id: "diamond",
    name: "Diamond",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/metatoy/Twitch_Drops_30_Min.jpg`,
  },
  {
    id: "immutable-dragon",
    name: "Immutable Dragon",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/metatoy/Twitch_Drops_1_Hour.jpg`,
  },
  {
    id: "treasure-chest",
    name: "Treasure Chest",
    description: "",
    image: `${appConfig.ASSET_BASE_URL}/images/twitch/metatoy/Twitch_Drops_2_Hour.jpg`,
  },
];

const OVERTAKE_CLIENT_ID = "sozeadcfdpuou7ejpy0oh61f7pdjgn";
const BATTLE_RISE_CLIENT_ID = "hjr3zp7t5plaijytge6aw4vou1zf3i";
const ELUMIA_CLIENT_ID = "cscpwxmj63izua75nxqg33mrrzxor7";
const ARMOURX_CLIENT_ID = "9fucy9w7fkjrf5oe6km0pg48eb6woi";
const METATOY_CLIENT_ID = "239ggrt18bsf62njcl0vf3snbz1x10";

const OVERTAKE_TWITCH_STREAMERS = [
  "CoreyBMTG",
  "RedBeard",
  "ixxdeee",
  "MYMTUMTUM69",
  "rcastiello",
  "RAPSOLO",
];

const BATTLE_RISE_TWITCH_STREAMERS = [
  "revolaimar",
  "srtumbao",
  "bale_gg",
  "pompeyo4",
  "0uahleouff",
  "erbacce_cr",
  "sergioramosgg",
  "drekzenn",
  "adrianpiedra",
  "rockstarcr",
  "yosoyricklive",
  "mafertejeda10",
  "daviderooney",
  "lapeceratonta",
  "light_e7",
  "thedarkraimola",
  "rale11play",
];

const ELUMIA_TWITCH_STREAMERS = [
  "revolaimar",
  "srtumbao",
  "bale_gg",
  "pompeyo4",
  "0uahleouff",
  "erbacce_cr",
  "sergioramosgg",
  "drekzenn",
  "adrianpiedra",
  "rockstarcr",
  "yosoyricklive",
  "mafertejeda10",
  "daviderooney",
  "lapeceratonta",
  "light_e7",
  "thedarkraimola",
  "rale11play",
];

const ARMOURX_TWITCH_STREAMERS = [
  "revolaimar",
  "srtumbao",
  "bale_gg",
  "pompeyo4",
  "0uahleouff",
  "erbacce_cr",
  "sergioramosgg",
  "drekzenn",
  "adrianpiedra",
  "rockstarcr",
  "yosoyricklive",
  "mafertejeda10",
  "daviderooney",
  "lapeceratonta",
  "light_e7",
  "thedarkraimola",
  "rale11play",
];

const METATOY_TWITCH_STREAMERS = [
  "kett",
  "peu_mc",
  "yosoyricklive",
  "pescocofino",
  "ocamargoluiz",
  "artubeclash",
  "revolaimar",
  "srtumbao",
  "bale_gg",
  "rockstarcr",
  "pompeyo4",
];

const getTwitchDrops = (game: string) => {
  switch (game) {
    case HELLMASTER_GAME_UID:
      return OVERTAKE_TWITCH_DROPS;
    case BATTLE_RISE_GAME_UID:
      return BATTLE_RISE_TWITCH_DROPS;
    case ELUMIA_GAME_UID:
      return ELUMIA_TWITCH_DROPS;
    case ARMOURX_GAME_UID:
      return ARMOURX_TWITCH_DROPS;
    case METATOY_GAME_UID:
      return METATOY_TWITCH_DROPS;
    default:
      return [];
  }
};

const getTwitchClientId = (game: string): string => {
  switch (game) {
    case HELLMASTER_GAME_UID:
      return OVERTAKE_CLIENT_ID;
    case BATTLE_RISE_GAME_UID:
      return BATTLE_RISE_CLIENT_ID;
    case ELUMIA_GAME_UID:
      return ELUMIA_CLIENT_ID;
    case ARMOURX_GAME_UID:
      return ARMOURX_CLIENT_ID;
    case METATOY_GAME_UID:
      return METATOY_CLIENT_ID;
    default:
      throw new Error(`Unknown game: ${game}`);
  }
};

const getTwitchStreamers = (game: string): string[] => {
  switch (game) {
    case HELLMASTER_GAME_UID:
      return OVERTAKE_TWITCH_STREAMERS;
    case BATTLE_RISE_GAME_UID:
      return BATTLE_RISE_TWITCH_STREAMERS;
    case ELUMIA_GAME_UID:
      return ELUMIA_TWITCH_STREAMERS;
    case ARMOURX_GAME_UID:
      return ARMOURX_TWITCH_STREAMERS;
    case METATOY_GAME_UID:
      return METATOY_TWITCH_STREAMERS;
    default:
      return [];
  }
};

const getTwitchDropsDate = (game: string): Date => {
  switch (game) {
    case HELLMASTER_GAME_UID:
      return OVERTAKE_TWITCH_DROPS_DATE;
    case BATTLE_RISE_GAME_UID:
      return BATTLE_RISE_TWITCH_DROPS_DATE;
    case ELUMIA_GAME_UID:
      return ELUMIA_TWITCH_DROPS_DATE;
    case ARMOURX_GAME_UID:
      return ARMOURX_TWITCH_DROPS_DATE;
    case METATOY_GAME_UID:
      return METATOY_TWITCH_DROPS_DATE;
    default:
      throw new Error(`Unknown game: ${game}`);
  }
};

function DropsDivider({
  children,
  sx = {},
}: { children: ReactNode; sx?: ComponentProps<typeof Divider>["sx"] }) {
  return (
    <Divider
      size="xSmall"
      sx={merge(
        {
          mt: { default: "base.spacing.x10", large: "base.spacing.x12" },
          mb: "base.spacing.x5",
        },
        sx,
      )}
    >
      {children}
    </Divider>
  );
}

function GameLinks({
  platforms,
  links,
  game,
  onHowToClick,
  showHowTo,
  howToOpenDropsButtonEnabled,
}: {
  platforms: string[];
  links: string[];
  game: Content.GameDocument;
  onHowToClick: () => void;
  showHowTo: boolean;
  howToOpenDropsButtonEnabled?: boolean;
}) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="flex-start"
      justifyContent="flex-start"
      gap="base.spacing.x4"
    >
      {platforms.map((platform, idx) => {
        const icon = mapPlatformIcons(platform);
        const link = links?.[idx];
        const platformLabel =
          game.uid === "elumia"
            ? platform === "Windows"
              ? "Windows via launcher"
              : "Android via launcher"
            : platform;

        if (!icon) return null;

        return game.uid === ARMOURX_GAME_UID ? (
          <>
            <Button
              key="armourx-hyperplay"
              onClick={() => {
                window.open(
                  "https://store.hyperplay.xyz/game/triumphgames/armourx",
                  "_blank",
                );
              }}
            >
              Hyperplay
            </Button>
            <Button
              key="armourx-ultra"
              onClick={() => {
                window.open(
                  "https://store.ultra.io/en/store/games/armourx/68107071e2d476adf32cfa8f",
                  "_blank",
                );
              }}
            >
              Ultra
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              trackLegacyEvent({
                userJourney: "Games",
                screen: "TwitchDrops",
                control: "Platform",
                controlType: "Button",
                action: "Pressed",
                extras: { platform, game: game.uid },
              });
            }}
            key={`${icon}-${link}`}
            rc={
              link ? (
                <NextLink
                  onClick={() => {}}
                  href={link}
                  target="_blank"
                  referrerPolicy="no-referrer"
                />
              ) : undefined
            }
          >
            <Button.Icon icon={icon} />
            {platformLabel}
          </Button>
        );
      })}
      {showHowTo && howToOpenDropsButtonEnabled && (
        <Button onClick={onHowToClick}>
          <Button.Icon icon="Play" />
          How to Open Twitch Drops
        </Button>
      )}
    </Stack>
  );
}

function TwitchFlavouredBiomeButton({
  onClick,
  children,
  size,
  disabled,
  sx = {},
}: {
  onClick?: () => void;
  children: ReactNode;
  size: ComponentProps<typeof Button>["size"];
  disabled?: boolean;
  sx?: ComponentProps<typeof Button>["sx"];
}) {
  return (
    <Button
      onClick={onClick}
      size={size}
      variant="primary"
      disabled={disabled}
      sx={merge(
        {
          bg: TWITCH_PURPLE,
          "&::before": { bg: TWITCH_PURPLE },
          "&.Button--medium:hover:not([disabled])": {
            boxShadow: `inset 0 0 0 3px ${TWITCH_PURPLE}`,
          },
          "&.Button--medium:active:not([disabled])": {
            boxShadow: `inset 0 0 0 3px ${TWITCH_PURPLE}`,
          },
          "&.Button--large:hover:not([disabled])": {
            boxShadow: `inset 0 0 0 4px ${TWITCH_PURPLE}`,
          },
          "&.Button--large:active:not([disabled])": {
            boxShadow: `inset 0 0 0 4px ${TWITCH_PURPLE}`,
          },
        },
        sx,
      )}
    >
      {children}
    </Button>
  );
}

type GameDropsLayoutProps = {
  defaultBgImage: string | null | undefined;
  game: Content.GameDocument;
  page: Content.PremiumGameDetailsPageDocument;
  gameSpecs: GameSpecifications;
};

export const OVERTAKE_TWITCH_DROPS_DATE = new Date("2025-05-17T06:00:00.000Z");
export const OVERTAKE_TWITCH_DROPS_END_DATE = new Date(
  "2025-05-26T06:00:00.000Z",
);

export const BATTLE_RISE_TWITCH_DROPS_DATE = new Date(
  "2025-07-31T00:00:00.000Z",
);
export const BATTLE_RISE_TWITCH_DROPS_END_DATE = new Date(
  "2025-08-10T06:00:00.000Z",
);

export const ELUMIA_TWITCH_DROPS_DATE = new Date("2025-08-14T09:00:00.000Z");
export const ELUMIA_TWITCH_DROPS_END_DATE = new Date(
  "2025-08-24T09:00:00.000Z",
);

export const ARMOURX_TWITCH_DROPS_DATE = new Date("2025-08-28T07:00:00.000Z");
export const ARMOURX_TWITCH_DROPS_END_DATE = new Date(
  "2025-09-07T06:59:00.000Z",
);

export const METATOY_TWITCH_DROPS_DATE = new Date("2025-11-01T12:00:00.000Z");
export const METATOY_TWITCH_DROPS_END_DATE = new Date(
  "2025-11-12T17:59:00.000Z",
);

const HOW_TO_DROPS_VIDEO: { url: string; mime: PlayVideoMimeType } = {
  url: `${appConfig.PLAY_ASSETS_CDN_BASE_URL}/hosted-assets/videos/somnis-rumble-rush/how-to-claim-drops.mp4`,
  mime: "video/mp4",
};

function UnwrappedGameDropsLayout({
  defaultBgImage,
  game,
  page,
  gameSpecs,
}: GameDropsLayoutProps) {
  const { base } = useTheme();
  const { overtakeHowToOpenDropsButtonEnabled } = useFlags();
  const {
    isLoggedIn,
    walletAddress,
    isLoggingIn: passportLoginLoading,
  } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const playLink = getWebLink(game.data.website).url;
  const pageTitle = game.data.name ?? "";
  const heroImageRef = useRef<HTMLImageElement | null>(null);
  const { isIntersecting, domRef: setIORef } = useIntersectionObserver();
  // Authorization code for OAuth flow
  const [authorizationCode, setAuthorizationCode] = useState<string | null>(
    null,
  );
  const previousAuthorizationCode = usePrevious(authorizationCode);
  const [twitchUsername, setTwitchUsername] = useState<string | undefined>(
    undefined,
  );
  const [loggingIntoTwitch, setLoggingIntoTwitch] = useState(false);
  const [showHowToVideo, setShowHowToVideo] = useState(false);
  const openHowToVideo = () => setShowHowToVideo(true);
  const closeHowToVideo = () => setShowHowToVideo(false);

  const getAccessToken = useAccessToken();
  const gameUUID = (game?.data?.games_service_item?.id as string) || "";
  const isLoggedInToTwitch = useMemo(
    () => isLoggedIn === true && !!twitchUsername,
    [isLoggedIn, twitchUsername],
  );

  const twitchDrops = getTwitchDrops(game.uid);
  const twitchClientId = getTwitchClientId(game.uid);
  const twitchStreamers = getTwitchStreamers(game.uid);
  const twitchDropsDate = getTwitchDropsDate(game.uid);

  const campaignNotStarted = useMemo(() => {
    return new Date() < twitchDropsDate;
  }, [twitchDropsDate]);

  useTopHeroScrollMotion({
    heroImageRef,
    isIntersecting,
  });
  const isSmallScreenMode = useIsLessThanMediumScreen();
  const onLoginClick = useCallback(() => {
    trackLegacyEvent({
      userJourney: "Games",
      screen: "TwitchDrops",
      control: "SignIn",
      controlType: "Button",
      action: "Pressed",
      extras: { path: window.location.pathname, game: game.uid },
    });
    openLoginModal();
  }, [openLoginModal, game.uid]);

  const { data: twitchDropsResponse, isLoading: isLoadingTwitchDrops } =
    useGetTwitchDrops(gameUUID, twitchClientId, isLoggedInToTwitch, game.uid);

  const hasClaimedDrops = useMemo(
    () => (twitchDropsResponse?.data?.length ?? 0) > 0,
    [twitchDropsResponse],
  );

  const dropStatus = useMemo(() => {
    return (
      twitchDropsResponse?.data?.reduce(
        (acc, drop) => {
          acc[drop.benefit_id] = drop.fulfillment_status;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {}
    );
  }, [twitchDropsResponse]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (!code) {
      return;
    }

    setAuthorizationCode(code);
  }, []);

  useEffect(() => {
    const fetchTwitchUsername = async () => {
      const user = await getTwitchUser(gameUUID);
      setTwitchUsername(user?.username);
    };
    fetchTwitchUsername();
  }, [gameUUID]);

  const handleTwitchLogin = useCallback(
    async (code: string) => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }
      setLoggingIntoTwitch(true);
      try {
        setTwitchUsername(
          await twitchLogin(accessToken, code, gameUUID, game.uid),
        );
      } catch (error) {
        notifyError(error, "twitchLogin");
      }

      // On successful connection, add the game to the user's wishlist
      try {
        await authorizedPost(
          "/v1/games/wishlist",
          { game_id: gameUUID },
          accessToken,
        );
      } catch (error) {
        // Ignore 400 errors as they are expected (already)
        if (error instanceof AxiosError && error.response?.status !== 400) {
          notifyError(error, "wishlist");
        }
      }
      setLoggingIntoTwitch(false);
    },
    [getAccessToken, gameUUID, game.uid],
  );

  useEffect(() => {
    if (authorizationCode && authorizationCode !== previousAuthorizationCode) {
      handleTwitchLogin(authorizationCode);
    }
  }, [authorizationCode, previousAuthorizationCode, handleTwitchLogin]);

  const onTwitchConnectClick = useCallback(async () => {
    trackLegacyEvent({
      userJourney: "Games",
      screen: "TwitchDrops",
      control: "ConnectTwitch",
      controlType: "Button",
      action: "Pressed",
      extras: { game: game.uid },
    });
    // Refactor to get the clientId based on the gameId
    const redirectUrl = `${appConfig.BASE_REDIRECT_URL}/twitch-drops/${game.uid}`;
    window.open(
      `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${twitchClientId}&redirect_uri=${redirectUrl}&scope=openid`,
      "_self",
    );
  }, [game.uid, twitchClientId]);

  const [isHowToDialogOpen, setIsHowToDialogOpen] = useState(false);
  const onCloseHowToDialog = useCallback(() => setIsHowToDialogOpen(false), []);

  return (
    <>
      {defaultBgImage && (
        <HeroBackground
          sx={{
            pos: "absolute",
            top: "0",
            left: "0",
            bradtl: "base.borderRadius.x8",
            bradtr: "base.borderRadius.x8",
          }}
          aspectRatio={{ default: "1", xLarge: "16/11" }}
        >
          <HeroBackground.Image
            loading="eager"
            use={
              <Image
                src={defaultBgImage}
                alt={defaultBgImage}
                fill
                priority
                sizes={mediaQuerySizesHelper([
                  { rule: "default", width: "100dvw - 16px" },
                  {
                    rule: "minWidth",
                    breakpoint: "medium",
                    width: `calc(100dvw - ${SIDEBAR_WIDTH} - 32px)`,
                  },
                ])({ base })}
              />
            }
          />
        </HeroBackground>
      )}
      <Stack
        sx={{ pos: "relative" }}
        testId="GameDetailPage__mainContainer"
        gap={{ default: "base.spacing.x2", medium: "base.spacing.x8" }}
      >
        <PremiumGameHeader
          playLink={playLink}
          game={game}
          stickyPageTitle={pageTitle}
          useMobileExperiment={isSmallScreenMode}
        />

        <Stack
          sx={{ px: { default: "none", large: "base.spacing.x12" } }}
          testId="heroSection"
          direction={{ default: "column", xLarge: "row" }}
          alignItems="flex-end"
          gap={{ default: "base.spacing.x8", large: "base.spacing.x12" }}
        >
          <Box
            sx={{ w: { default: "100%", xLarge: "28%" } }}
            testId="heroSection__rightSide"
          >
            {page.data.overlay_image.url ? (
              <Box
                sx={{
                  // @TODO: for small screens, displaying these characters is alot harder (they have a different crop)
                  d: { default: "none", large: "block" },
                  w: {
                    default: "100%",
                    large: "36dvw",
                    xLarge: "33dvw",
                    xxLarge: "30dvw",
                    xxxLarge: "26dvw",
                  },
                  pos: { default: "static", large: "absolute" },
                  right: {
                    default: "unset",
                    large: `calc(${base.spacing.x10} * -1)`,
                  },
                  top: { default: "unset", large: "base.spacing.x22" },
                  objectFit: "contain",
                  objectPosition: "right center",
                  pointerEvents: "none",
                  userSelect: "none",
                  // @NOTE: all images used here MUST be 8/16 aspect ratio
                  aspectRatio: "8/13",
                }}
              >
                <Image
                  src={page.data.overlay_image.url}
                  loading="eager"
                  alt={page.data.overlay_image.alt ?? ""}
                  priority
                  fill
                  ref={(el) => {
                    if (!el) return;
                    setIORef(el);
                    heroImageRef.current = el;
                  }}
                  data-testid="heroOverlayImage"
                  sizes={mediaQuerySizesHelper([
                    { rule: "default", width: "90vw" },
                    {
                      rule: "minWidth",
                      breakpoint: "large",
                      width: "37vw",
                    },
                    {
                      rule: "minWidth",
                      breakpoint: "xxLarge",
                      width: "32vw",
                    },
                  ])({ base })}
                />
              </Box>
            ) : null}
          </Box>
        </Stack>

        <Box
          testId="contentSection"
          sx={{
            px: { default: "none", large: "base.spacing.x12" },
            w: { default: "100%", large: "70%" },
            // @NOTE: this prevents flickering effect when the header gets stuck inbetween
            // stuck and unstuck (only happens on a very specific sized screen)
            minh: { default: "unset", medium: "90dvh" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Heading
              size={["medium", null, null, "large"]}
              sx={{ whiteSpace: "nowrap" }}
            >
              Twitch Drops
            </Heading>
            <Button
              size="small"
              variant="tertiary"
              onClick={() => setIsHowToDialogOpen(true)}
            >
              <Button.Icon icon="AirDrop" />
              Participation Info
            </Button>
          </Stack>
          <Body rc={<p />} sx={{ mt: "base.spacing.x5" }} size="large">
            A new way to earn rewards simply by watching{" "}
            <Link
              rc={
                <a
                  href={
                    game.uid === HELLMASTER_GAME_UID
                      ? "http://www.twitch.tv/directory/category/hellmaster?filter=drops"
                      : game.uid === ELUMIA_GAME_UID
                        ? "https://www.twitch.tv/directory/category/legends-of-elumia?filter=drops"
                        : game.uid === ARMOURX_GAME_UID
                          ? "https://www.twitch.tv/directory/category/armourx?filter=drops"
                          : game.uid === METATOY_GAME_UID
                            ? "https://www.twitch.tv/directory/category/meta-toy-dragonz-saga"
                            : "http://www.twitch.tv/directory/category/battlerise-kingdom-of-champions?filter=drops"
                  }
                  target="_blank"
                  rel="noreferrer"
                />
              }
            >
              Twitch
            </Link>{" "}
            streams of {game.data.name}.
          </Body>
          <Body rc={<p />} sx={{ mt: "base.spacing.x4" }}>
            To get started, connect your Twitch account to your Immutable Play
            account.
          </Body>
          {!!campaignNotStarted && (
            <Stack
              direction="column"
              gap="base.spacing.x2"
              sx={{ mt: "base.spacing.x4" }}
            >
              <Body>The drops campaign will start in:</Body>
              <CountdownTimer
                futureTarget={twitchDropsDate}
                size="medium"
                variant="inverse"
                justifyContent={"flex-start"}
              />
            </Stack>
          )}

          <DropsDivider
            sx={{
              ...(isLoggedInToTwitch
                ? { c: "base.color.status.success.bright" }
                : {}),
              ...hFlex,
              ...centerFlexChildren,
              gap: "base.spacing.x2",
            }}
          >
            {isLoggedInToTwitch ? (
              <Icon
                icon="TickWithCircle"
                variant="bold"
                sx={{
                  fill: "base.color.status.success.bright",
                  w: "base.icon.size.200",
                }}
              />
            ) : null}
            Account connection
          </DropsDivider>
          <AnimateHeightBox
            innerContainerSx={{
              ...hFlex,
              flexWrap: "wrap",
              gap: "base.spacing.x4",
              w: "100%",
            }}
          >
            {isLoggedInToTwitch ? (
              <>
                <Chip
                  label={
                    <>
                      Twitch: <strong>{twitchUsername}</strong>
                    </>
                  }
                >
                  <Chip.Icon
                    icon="Twitch"
                    sx={{
                      fill: TWITCH_PURPLE,
                      w: "base.icon.size.200",
                    }}
                  />
                </Chip>
              </>
            ) : (
              <Stack direction="column" gap="base.spacing.x4">
                {!isLoggedIn && (
                  <Body
                    sx={{
                      fontSize: "base.text.body.small.regular.fontSize",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    Login to Passport first and then connect your Twitch
                    account.
                  </Body>
                )}
                <Stack direction="row" gap="base.spacing.x4">
                  {!isLoggedIn && (
                    <Button
                      onClick={onLoginClick}
                      size={["medium", null, null, null, "large"]}
                    >
                      {passportLoginLoading ? (
                        <>
                          <Button.Icon icon="Loading" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <Button.Logo logo="PassportSymbol" />
                          Sign in
                        </>
                      )}
                    </Button>
                  )}
                  <TwitchFlavouredBiomeButton
                    onClick={onTwitchConnectClick}
                    size={["medium", null, null, null, "large"]}
                    // Wallet Address is required for the Twitch linking
                    disabled={!walletAddress || !isLoggedIn}
                    sx={{ w: "fit-content" }}
                  >
                    {loggingIntoTwitch ? (
                      <>Connecting...</>
                    ) : isLoggedIn && !walletAddress ? (
                      <>
                        <Button.Icon icon="Loading" />
                        Creating your wallet...
                      </>
                    ) : (
                      <>
                        <Button.Icon icon="Twitch" />
                        Connect Twitch
                      </>
                    )}
                  </TwitchFlavouredBiomeButton>
                </Stack>
                {isLoggedIn && (
                  <Body
                    sx={{
                      fontSize: "base.text.body.small.regular.fontSize",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    Connecting Twitch will also follow {game.data.name} in
                    Immutable Play
                  </Body>
                )}
              </Stack>
            )}
          </AnimateHeightBox>

          <DropsDivider>Available twitch drops</DropsDivider>

          <Grid minColumnWidth="218px" sx={{ mb: "base.spacing.x4" }}>
            {twitchDrops.map((drop, index) => {
              const status = dropStatus[drop.id];
              return (
                <HeroCard
                  key={drop.id}
                  size="small"
                  aspectRatio="1/1"
                  sx={{
                    position: "relative",
                    "&::after": !status
                      ? {
                          content: '""',
                          position: "absolute",
                          top: "0",
                          left: "0",
                          right: "0",
                          bottom: "0",
                          background: "rgba(0, 0, 0, 0.4)",
                          zIndex: 1,
                        }
                      : undefined,
                  }}
                >
                  <HeroCard.AssetImage
                    imageUrl={drop.image ?? ""}
                    alt={drop.name}
                    relativeImageSizeInLayout="240px"
                  />
                  <HeroCard.Title size="small">{drop.name}</HeroCard.Title>
                  {status && (
                    <HeroCard.Chip
                      label={status === "CLAIMED" ? "Claimed" : "Fulfilled"}
                    >
                      <HeroCard.Chip.Icon
                        icon={
                          status === "CLAIMED" ? "Trophy" : "TickWithCircle"
                        }
                        variant="bold"
                      />
                    </HeroCard.Chip>
                  )}
                </HeroCard>
              );
            })}
          </Grid>
          <Stack direction="column" gap="base.spacing.x4">
            {(!isLoggedIn || !twitchUsername) && (
              <Body size="small">
                Please sign in with Passport &amp; connect your Twitch account
                to see your earned drops.
              </Body>
            )}
            <Body size="small">
              Claimed drops will be distributed every few days.
            </Body>
          </Stack>

          {isLoggedInToTwitch && (
            <>
              <DropsDivider>Play {game.data.name}</DropsDivider>
              <GameLinks
                game={game}
                platforms={gameSpecs.platforms.map(([name]) => name)}
                links={gameSpecs.platforms.map(([_, link]) => link)}
                onHowToClick={openHowToVideo}
                // only show how to for somnis-rumble-rush
                showHowTo={hasClaimedDrops && game.uid === "somnis-rumble-rush"}
                howToOpenDropsButtonEnabled={
                  overtakeHowToOpenDropsButtonEnabled
                }
              />
            </>
          )}

          <DropsDivider>Official Streamers</DropsDivider>

          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="flex-start"
            justifyContent="flex-start"
            gap="base.spacing.x4"
          >
            {twitchStreamers?.map((streamer) => (
              <Button
                variant="secondary"
                key={streamer}
                onClick={() => {
                  window.open(`https://www.twitch.tv/${streamer}`, "_blank");
                }}
              >
                <Button.Icon icon="Twitch" />
                {streamer}
              </Button>
            ))}
          </Stack>
        </Box>
      </Stack>

      <ConfirmationDialog
        visible={isHowToDialogOpen}
        onCloseModal={onCloseHowToDialog}
      >
        <ConfirmationDialog.Content textAlign="left" sx={{ maxw: "500px" }}>
          <ConfirmationDialog.Content.CloseButtCon
            onClick={onCloseHowToDialog}
          />
          <ConfirmationDialog.Content.Title>
            How to participate
          </ConfirmationDialog.Content.Title>
          <ConfirmationDialog.Content.Caption sx={{ mt: "base.spacing.x5" }}>
            <BulletList use={<ol />}>
              <BulletList.Item size="small">
                Sign in with your Immutable Passport.
              </BulletList.Item>
              <BulletList.Item size="small">
                Log in with Twitch.
              </BulletList.Item>
              <BulletList.Item size="small">
                Go to a participating live channel (official streamers).
              </BulletList.Item>
              <BulletList.Item size="small">
                Watch {game.data.name} and claim the rewards
              </BulletList.Item>
            </BulletList>
          </ConfirmationDialog.Content.Caption>
          <ConfirmationDialog.Content.Caption sx={{ mt: "base.spacing.x3" }}>
            Claimed drops will be distributed every few days.
          </ConfirmationDialog.Content.Caption>
        </ConfirmationDialog.Content>
      </ConfirmationDialog>

      {showHowToVideo && game.uid !== "battlerise" && (
        <VideoQuestModal
          videoUrl={HOW_TO_DROPS_VIDEO.url}
          videoMimeType={HOW_TO_DROPS_VIDEO.mime}
          onClose={closeHowToVideo}
          onComplete={closeHowToVideo}
          muted={true}
        />
      )}
    </>
  );
}

export const GameDropsLayout = dynamic(
  () => Promise.resolve(UnwrappedGameDropsLayout),
  { ssr: false },
);
